import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { GoogleMap, LoadScript, StreetViewPanorama, Marker, StandaloneSearchBox } from '@react-google-maps/api';

const DEFAULT_POSITION = { lat: -33.9249, lng: 18.4241 }; // Cape Town

const containerStyle = {
  width: '100%',
  height: '100%'
};

const SearchMap = () => {
  const { state } = useLocation();
  const category = state?.category || 'Unknown';
  const reason = state?.reason || '';
  const [address, setAddress] = useState('');
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [showStreetView, setShowStreetView] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const searchBoxRef = useRef(null);

  // Prompt for user's location on mount
  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setPosition({ lat, lng });
          setAddress(`Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`);
        },
        err => {
          // fallback: keep default position
        }
      );
    }
  }, []);

  const handleAddressChange = e => {
    setAddress(e.target.value);
    console.log('Address input changed:', e.target.value); // Placeholder for future API call
  };

  const handlePlacesChanged = () => {
    const searchBox = searchBoxRef.current;
    if (searchBox) {
      const places = searchBox.getPlaces();
      if (places && places.length > 0) {
        const place = places[0];
        if (place.geometry && place.geometry.location) {
          const loc = place.geometry.location;
          setPosition({ lat: loc.lat(), lng: loc.lng() });
          setAddress(place.formatted_address || place.name);
          setShowStreetView(true);
        }
      }
    }
  };
  const handleAddressSubmit = e => {
    e.preventDefault();
    if (!mapLoaded || !window.google || !window.google.maps) {
      alert('Map is not loaded yet. Please try again in a moment.');
      return;
    }
    // Geocode address using Google Maps Geocoding API
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setPosition({ lat: loc.lat(), lng: loc.lng() });
        setShowStreetView(true);
      } else {
        alert('Address not found');
      }
    });
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
      libraries={["places"]}
      onLoad={() => setMapLoaded(true)}
    >
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-grow">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">Selected Field of Law</h2>
                <div className="rounded-lg border p-4 bg-gray-50 mb-4">
                  <p className="text-lg font-semibold text-blue-700">{category}</p>
                  {reason && <p className="text-sm text-gray-600 mt-1">{reason}</p>}
                </div>
              </div>
              {/* Address bar above the map */}
              <div className="mb-4">
                <div className="relative">
                  <StandaloneSearchBox
                    onLoad={ref => (searchBoxRef.current = ref)}
                    onPlacesChanged={handlePlacesChanged}
                  >
                    <input
                      className="w-full pl-12 pr-4 py-3 bg-white rounded-full shadow-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-shadow"
                      placeholder="Search for a location or address"
                      type="text"
                      value={address}
                      onChange={handleAddressChange}
                    />
                  </StandaloneSearchBox>
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">search</span>
                </div>
              </div>
              <div className="aspect-[16/9] rounded-lg overflow-hidden relative shadow-lg mb-8">
                {!showStreetView ? (
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={position}
                    zoom={15}
                  >
                    {/* Pin user's location */}
                    <Marker position={position} />
                  </GoogleMap>
                ) : (
                  <StreetViewPanorama
                    position={position}
                    visible={true}
                    options={{ addressControl: false }}
                    mapContainerStyle={containerStyle}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </LoadScript>
  );
};

export default SearchMap;
